/**
 * useScrollTuck — tuck the shell's top bar away while the content is
 * scrolled down, and bring it back the moment the reader heads up.
 *
 * On a 390px phone the top bar wraps into three rows (site and LIVE; the
 * icon cluster; the robot / task stat chips) and costs a fifth of the
 * screen on every page. Tucked, it is one slim row -- menu, site, the
 * phone's fire control, the alerts bell and the avatar -- and the rest
 * comes back on any upward scroll (phone header option C, 2026-10-30).
 * On a laptop the bar goes from 66px to 44px: site, LIVE, bell and
 * avatar stay; the stat chips, mute, help, favourites and the signed-in
 * name give way (laptop version approved the same day).
 *
 * WHAT HIDES IS CSS'S BUSINESS. This hook only decides the state; the
 * shell puts it on `.nx-shell` as `data-bar-tucked` and everything that
 * gives way is marked `data-tuck="hide"` (`AppShell.css`). A screen can
 * opt in the same way (the Tasks title row) or key a sticky header off
 * the attribute. Nothing outside the bar's own rows is marked: the fire
 * alarm overlay, the global status banner, the "viewing as" banner and
 * the fire-mode hold notice are never hidden.
 *
 * WHERE IT RUNS IS `enabled`, AND ONLY THERE (`AppShell.tsx`): every
 * width since the laptop version. The CSS keys on the attribute; its
 * media queries only decide WHAT gives way at each width (LIVE is
 * `data-tuck="hide-phone"`: it goes on a phone and stays on a laptop).
 *
 * Rules:
 *  - SCROLL, in the capture phase on `scrollRoot`: scroll events do not
 *    bubble, but capture still runs on every ancestor, so one listener
 *    hears the page (`.nx-shell__content`) AND every scroll box inside
 *    it. On a laptop that inner box is the usual case: a page-fill
 *    table's own scroller, the calendar lanes' scroller. Everything --
 *    the position, the direction, the room-to-scroll guard -- is read
 *    from the EVENT TARGET, the box that actually moved, never from the
 *    page around it. Each box's last position is kept separately, so
 *    two boxes never read as one jumping scroll.
 *  - Tucks after scrolling DOWN past `TUCK_AFTER_PX` (with a few px of
 *    hysteresis), and only where there is room to scroll well beyond
 *    what the tuck gives back -- otherwise the box shrinks to fit, the
 *    browser clamps it to the top, and the bar would flap.
 *  - Restores on any UPWARD scroll beyond `RESTORE_AFTER_UP_PX`, on
 *    reaching the top, on keyboard focus landing in anything the tuck
 *    really hid (a `data-tuck` element drawn at 1px or less -- LIVE is
 *    marked but stays visible on a laptop), and on `resetKey` changing
 *    (a new page starts with the bar).
 *  - For `SETTLE_MS` after each change it only records positions: the
 *    bar changing height moves the content and can clamp a scroller,
 *    and those scroll events are the layout's, not the reader's.
 *  - Scrolls inside a dialog (`[role="dialog"]`, a phone sheet) or
 *    anything marked `data-tuck-ignore` are not the page's and are
 *    ignored; horizontal scrolls change nothing. A box marked
 *    `data-tuck-quiet` while the page itself scrolls it (the Agenda
 *    opening at now) has its position recorded and nothing else.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';

export const TUCK_AFTER_PX = 48;
export const TUCK_HYSTERESIS_PX = 4;
export const RESTORE_AFTER_UP_PX = 8;
export const SETTLE_MS = 250;
/** How far a box must be able to scroll before tucking is worth it:
 *  more than the rows the tuck gives back, so it is never clamped to
 *  the top by the bar getting shorter. */
export const MIN_SCROLL_ROOM_PX = 200;

const IGNORE = '[role="dialog"], [aria-modal="true"], [data-tuck-ignore]';
/** A box the PAGE is scrolling (the Agenda opening at now): its position
 *  is recorded, so the reader's next scroll is measured from where it
 *  really is, but the move itself is not the reader's and tucks nothing. */
const QUIET = '[data-tuck-quiet]';
const MARKED = '[data-tuck]';

/** Did the tuck actually hide `el`? Hidden is drawn at 1px (`AppShell.css`);
 *  anything bigger is on screen. jsdom draws everything at 0. */
function hiddenByTuck(el: Element): boolean {
  const marked = el.closest(MARKED);
  if (!marked) return false;
  const box = marked.getBoundingClientRect();
  return box.width <= 1 && box.height <= 1;
}

/** `rest`: never tucked since the last reset. `restored`: was tucked and
 *  has come back (the shell animates that, motion permitting). */
export type TuckState = 'rest' | 'tucked' | 'restored';

export interface ScrollTuckOptions {
  /** Off: never tucks, and a tucked bar comes back at once. */
  enabled: boolean;
  /** A change restores the bar and forgets every position. */
  resetKey?: unknown;
}

export function useScrollTuck(
  scrollRoot: RefObject<HTMLElement | null>,
  focusRoot: RefObject<HTMLElement | null>,
  { enabled, resetKey }: ScrollTuckOptions,
): TuckState {
  const [state, setState] = useState<TuckState>('rest');
  const tuckedRef = useRef(false);
  // Bumped on every reset; the listeners below read it so positions
  // recorded before a reset are forgotten.
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    tuckedRef.current = false;
    setState('rest');
    setGeneration((g) => g + 1);
  }, [resetKey, enabled]);

  useEffect(() => {
    const root = scrollRoot.current;
    const focusEl = focusRoot.current;
    if (!enabled || !root) return undefined;

    const last = new WeakMap<Element, number>();
    let down = 0;
    let up = 0;
    let settleUntil = 0;

    const set = (tucked: boolean) => {
      if (tucked === tuckedRef.current) return;
      tuckedRef.current = tucked;
      down = 0;
      up = 0;
      settleUntil = Date.now() + SETTLE_MS;
      setState(tucked ? 'tucked' : 'restored');
    };

    const onScroll = (e: Event) => {
      const el = e.target;
      if (!(el instanceof Element) || el.closest(IGNORE)) return;
      const top = el.scrollTop;
      const prev = last.get(el) ?? 0;
      last.set(el, top);
      if (el.closest(QUIET)) return;
      const delta = top - prev;
      // A sideways scroll (a chip row) leaves scrollTop where it was.
      if (delta === 0 || Date.now() < settleUntil) return;
      if (top <= 0) {
        set(false);
        return;
      }
      // The box that moved, not the page: on a laptop the page rarely
      // scrolls at all and the table or the lanes do.
      const room = el.scrollHeight - el.clientHeight;
      // Settling back from an overscroll (iOS bounce at the bottom).
      if (prev > room) return;
      if (delta > 0) {
        down += delta;
        up = 0;
        if (
          !tuckedRef.current &&
          top > TUCK_AFTER_PX &&
          down > TUCK_HYSTERESIS_PX &&
          room > MIN_SCROLL_ROOM_PX
        ) {
          set(true);
        }
      } else {
        up -= delta;
        down = 0;
        if (tuckedRef.current && up > RESTORE_AFTER_UP_PX) set(false);
      }
    };

    const onFocusIn = (e: FocusEvent) => {
      if (!tuckedRef.current) return;
      const el = e.target;
      if (el instanceof Element && hiddenByTuck(el)) set(false);
    };

    root.addEventListener('scroll', onScroll, { capture: true, passive: true });
    focusEl?.addEventListener('focusin', onFocusIn);
    return () => {
      root.removeEventListener('scroll', onScroll, { capture: true });
      focusEl?.removeEventListener('focusin', onFocusIn);
    };
  }, [enabled, scrollRoot, focusRoot, generation]);

  return state;
}
